/**
 * Farmer drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const farmerDrawingsPack: ToilePack = {
    slug: 'farmer-drawings',
    category: 'coloring',
    name: 'Farmer drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'plough', label: 'Ploughing', subject: 'a farmer ploughing a field with two bullocks under a hot sun'},
        {key: 'harvest', label: 'Harvest', subject: 'a farmer cutting wheat with a sickle, sheaves standing in rows'},
        {key: 'tractor', label: 'Tractor', subject: 'a farmer driving a tractor beside a green field and a hut'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a scarecrow', 'with oxen', 'a wheat field'],
    directionHint: 'try: a scarecrow',
    seo: {
        title: 'Farmer Drawings: Ploughing, Harvest, Tractor',
        metaDescription: 'Nine farmer drawings: a farmer ploughing with bullocks, harvesting wheat and driving a tractor, in pencil, colour and outline.',
        h1: 'Farmer drawings: ploughing, harvesting, farmer on a tractor',
        tagline: 'Three scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Farmer drawings for a competition, a farming chart or a class project. The set shows a farmer ploughing a field with two bullocks, a farmer cutting wheat with a sickle with sheaves in rows, and a farmer driving a tractor beside a green field and a hut. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as a scarecrow, with oxen or a wheat field.',
        useCases: [
            'a farming chart for a school project',
            'a colouring page on farm work',
            'a competition entry on farmers',
            'a classroom display on food and farming',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Which picture shows modern farming?', a: 'The tractor scene shows machine farming. The ploughing and harvest scenes show work with bullocks and a sickle.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
